//src/hooks/useGroups.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createGroup,
  deleteGroup,
  getGroupDetail,
  listGroups,
  listPermissions,
  patchGroupStatus,
  updateGroup,
  updateGroupPermissions,
  type GroupPermissionsUpdateIn,
  type GroupStatusPatchIn,
  type GroupUpsertIn,
} from "@/services/groups";

export function useGroups(includeInactive = false) {
  return useQuery({
    queryKey: ["groups", { includeInactive }],
    queryFn: () => listGroups(includeInactive),
  });
}

export function useGroupDetail(groupId?: string) {
  return useQuery({
    queryKey: ["group_detail", groupId],
    queryFn: () => getGroupDetail(groupId as string),
    enabled: !!groupId,
  });
}

export function usePermissionsCatalog(enabled = true) {
  return useQuery({
    queryKey: ["permissions_catalog"],
    queryFn: () => listPermissions(),
    staleTime: 60_000,
    enabled,
  });
}

export function useCreateGroup() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (payload: GroupUpsertIn) => createGroup(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["groups"] });
    },
  });
}

export function useUpdateGroup() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: GroupUpsertIn }) =>
      updateGroup(id, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["groups"] });
      qc.invalidateQueries({ queryKey: ["group_detail", vars.id] });
    },
  });
}

export function usePatchGroupStatus() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: GroupStatusPatchIn }) =>
      patchGroupStatus(id, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["groups"] });
      qc.invalidateQueries({ queryKey: ["group_detail", vars.id] });
    },
  });
}

export function useDeleteGroup() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (groupId: string) => deleteGroup(groupId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["groups"] });
    },
  });
}

export function useUpdateGroupPermissions() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: GroupPermissionsUpdateIn;
    }) => updateGroupPermissions(id, body),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["groups"] });
      qc.invalidateQueries({ queryKey: ["group_detail", vars.id] });
    },
  });
}
